import { Box, Stack } from '@mui/material';
import type React from 'react';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../../../actions/common.actionTypes';
import { spaces } from '../../../../../assets/styles/themes/tokens';
import {
	getConversationDocument,
	getConversationStream,
	getConversationStreamActiveToolName,
	getCurrentConversation,
} from '../../../../../reducers/aiAssistant.selectors';
import services from '../../../../../services';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../../state/AIAssistantContextProvider';
import {
	AIAssistantEditorMode,
	aiAssistantEditorContext,
} from '../../../../../state/AIAssistantEditorProvider';
import { downloadHtmlAsDocx, downloadHtmlAsPdf } from '../../../../../utils/documentExport';
import { getAIAssistantPageSource } from '../../../../../utils/getAIAssistantPageSource';
import { hasIncompleteTrailingImageMarkdown } from '../../../../../utils/markdown';
import useRouter from '../../../../../utils/useRouter';
import useScreenSize from '../../../../../utils/useScreenSize';
import ChatMessageArtifactCard from '../../../../cards/ChatMessageArtifactCard';
import ExtendedChatMessageArtifactCard from '../../../../cards/ChatMessageArtifactCard/ExtendedChatMessageArtifactCard';
import { convertMarkdownToHtmlForPdf } from '../../../../utils/contentConverter';
import { useAIAssistantLatestMessageHeight } from '../../../Dialog/hooks/useAIAssistantLatestMessageHeight';
import { TOOL_NAME_GENERATE_IMAGE, TOOL_NAME_GENERATE_PODCAST } from '../../constants';
import { sanitizeFilename } from '../../utils';
import AIAssistantGeneratingIndicator from './AIAssistantGeneratingIndicator';
import AIAssistantMessage from './AIAssistantMessage';

const AIAssistantStreamedMessage: React.FC = () => {
	const { t } = useTranslation();
	const currentConversation = useSelector(getCurrentConversation);
	const stream = useSelector(getConversationStream);
	const streamDocument = useSelector(getConversationDocument);
	const activeToolName = useSelector(getConversationStreamActiveToolName);
	const isToolGeneratingImage = activeToolName === TOOL_NAME_GENERATE_IMAGE;
	const hasStreamingImagePlaceholder =
		!!stream.message && hasIncompleteTrailingImageMarkdown(stream.message.content[0].text);
	const isGeneratingImage = isToolGeneratingImage || hasStreamingImagePlaceholder;
	const isGeneratingPodcast = activeToolName === TOOL_NAME_GENERATE_PODCAST;
	const { isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
	const isLargeScreen = isSmScreen || isMdScreen || isLgScreen;
	const { displayMode } = useContext(aiAssistantContext);
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
	const isLoading = currentConversation?.status === AsyncActionStatus.LOADING;
	const isStreaming = stream.status === AsyncActionStatus.LOADING;
	const {
		match: {
			location: { pathname },
		},
	} = useRouter();
	const {
		isTextEditorOpen,
		mode: textEditorMode,
		openTextEditor,
		documentId: textEditorDocumentId,
	} = useContext(aiAssistantEditorContext);

	const messageHeight = useAIAssistantLatestMessageHeight();
	const showStreaming =
		(isStreaming && stream.conversationId === currentConversation?.data?.id) || isLoading;

	const isDocumentGenerating = streamDocument && streamDocument.status === 'started';
	const getStreamingCopy = () => {
		if (isGeneratingImage) return t('An image is being generated...');
		if (isGeneratingPodcast) return t('The podcast is being created...');
		if (isDocumentGenerating) return t('Your document is being generated...');
		return t('Your answer will be generated...');
	};
	const streamingCopy = getStreamingCopy();

	const showDocumentCard = streamDocument && streamDocument.status !== 'stopped';

	const handleDocumentCardClick = () => {
		if (!currentConversation?.data?.id || !streamDocument?.id) return;
		const pageSource = getAIAssistantPageSource(pathname);
		openTextEditor();
		services.track.ampliTrackTextEditorOpenedEvent(
			currentConversation.data.id,
			'document',
			pageSource
		);
	};

	const getStreamDownloadData = () => {
		const content = streamDocument?.content;
		if (!content) return null;
		const filename = streamDocument?.title || 'document';
		const html = convertMarkdownToHtmlForPdf(content);
		const sanitizedFilename = sanitizeFilename(filename);
		return { html, sanitizedFilename };
	};

	const handleDownloadPdf = async () => {
		const data = getStreamDownloadData();
		if (data) {
			await downloadHtmlAsPdf(data.html, data.sanitizedFilename);
			if (currentConversation?.data?.id) {
				services.track.ampliTrackTextEditorFileExportedEvent({
					conversationId: currentConversation.data.id,
					exportType: 'download',
					exportFormat: 'pdf',
					feature: ['document generation'],
				});
			}
		}
	};

	const handleDownloadDocx = async () => {
		const data = getStreamDownloadData();
		if (data) {
			await downloadHtmlAsDocx(data.html, data.sanitizedFilename);
			if (currentConversation?.data?.id) {
				services.track.ampliTrackTextEditorFileExportedEvent({
					conversationId: currentConversation.data.id,
					exportType: 'download',
					exportFormat: 'docx',
					feature: ['document generation'],
				});
			}
		}
	};

	useEffect(() => {
		if (textEditorMode === AIAssistantEditorMode.STREAM && streamDocument) {
			openTextEditor({ documentId: streamDocument.id });
		}
	}, [streamDocument, textEditorMode, openTextEditor]);

	return showStreaming ? (
		<Box data-testid="ai-assistant-streamed-message-spacer" sx={{ minHeight: messageHeight }}>
			{/* Also shown while a document streams: the model may stream text ahead
          of the document, and the indicator must survive those chunks. */}
			{((isLoading && !isStreaming) ||
				(isStreaming && isDocumentGenerating && !isGeneratingImage)) && (
				<AIAssistantGeneratingIndicator
					text={streamingCopy}
					dataTestId="message-is-being-generated-text"
				/>
			)}

			{showDocumentCard && (
				<Stack gap={spaces.sm} sx={{ marginBottom: spaces.md }}>
					{isLargeScreen && isPageMode && !isTextEditorOpen ? (
						<ExtendedChatMessageArtifactCard
							document={streamDocument}
							onEdit={handleDocumentCardClick}
							onDownloadPdf={handleDownloadPdf}
							onDownloadDocx={handleDownloadDocx}
						/>
					) : (
						<ChatMessageArtifactCard
							title={streamDocument.title || t('Generating document...')}
							subtitle={t('Click to edit')}
							onClick={handleDocumentCardClick}
							isActive={isTextEditorOpen && textEditorDocumentId === streamDocument.id}
						/>
					)}
				</Stack>
			)}

			{isStreaming && stream.message && (
				<AIAssistantMessage
					message={stream.message}
					isLast={false}
					withRegenerateButton={false}
					withFooter={false}
					isStreaming={isStreaming}
				/>
			)}

			{isGeneratingImage && isStreaming && (
				<AIAssistantGeneratingIndicator
					text={streamingCopy}
					dataTestId="image-is-being-generated-text"
				/>
			)}
		</Box>
	) : null;
};

export default AIAssistantStreamedMessage;
